import { Children, useEffect, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ReferralCprBadge } from '@/components/referrals/ReferralCprBadge';
import { referralLetterFrameClass } from '@/components/referrals/ReferralLetterDetails';
import { Card } from '@/components/ui/Card';
import type { Referral } from '@/hooks/useReferrals';
import { useReferralLetter } from '@/hooks/useReferrals';
import { formatReferralDateTime } from '@/lib/referral/referralDateTime';
import { medcomIdentifierDisplay, referralSenderIdentity } from '@/lib/referral/senderIdentifier';
import { cn } from '@/utils/cn';
import { referralOverviewRuleClass } from './referralOverviewLayout';

const STORAGE_KEY = 'clinio-v2-referral-overview-collapsed';

function medcomRelationKey(
  code: string | null,
):
  | 'referrals.medcom_relation_mother'
  | 'referrals.medcom_relation_father'
  | 'referrals.medcom_relation_child'
  | 'referrals.medcom_relation_unspecified'
  | null {
  switch (code) {
    case 'mor':
      return 'referrals.medcom_relation_mother';
    case 'far':
      return 'referrals.medcom_relation_father';
    case 'barn':
      return 'referrals.medcom_relation_child';
    case 'uspec_paaroerende':
      return 'referrals.medcom_relation_unspecified';
    default:
      return null;
  }
}

function formatLetterSenderAddress(sender: {
  street_name: string | null;
  suburb_name: string | null;
  post_code: string | null;
  district_name: string | null;
}): string | null {
  return [
    sender.street_name,
    sender.suburb_name,
    [sender.post_code, sender.district_name].filter(Boolean).join(' '),
  ]
    .filter((part) => part && part.trim().length > 0)
    .join(', ') || null;
}

function IdentifierLine({
  identifierCode,
  identifier,
  fallbackIdentifierCode,
}: {
  identifierCode: string | null;
  identifier: string | null;
  fallbackIdentifierCode?: string | null;
}) {
  const { t } = useTranslation();
  const display = medcomIdentifierDisplay(identifier, identifierCode, { fallbackIdentifierCode });
  if (!display) return null;
  return (
    <div className="text-xs text-ds-muted" data-testid="referral-overview-identifier-line">
      {t(display.headingKey)} {display.value}
    </div>
  );
}

/**
 * Henvisningsoplysninger: the same fields and collapse as on the standalone
 * henvisning page, in the same card as the patient Henvisning box — an outer
 * card and the letter frame inside it.
 */
export function ReferralOverviewCard({
  referral,
  canRevealCpr,
}: {
  referral: Referral;
  canRevealCpr: boolean;
}) {
  const { t } = useTranslation();
  const medcom = referral.source === 'medcom';
  const letterQuery = useReferralLetter(referral.id, medcom);
  const letter = letterQuery.data?.letter ?? null;
  const fileSender = letter?.sender ?? null;
  const fileReferrer = letter?.referrer ?? null;
  const fileRelative = letter?.relative ?? null;
  const storedSender = referralSenderIdentity(referral);
  const senderIdentity = {
    identifier: storedSender.identifier ?? fileSender?.identifier ?? null,
    identifierCode: storedSender.identifierCode ?? fileSender?.identifier_code ?? null,
  };
  const replacementCpr = letterQuery.data?.patient_id_kind === 'erstatnings_cpr';
  const relativeName = fileRelative
    ? [fileRelative.given_name, fileRelative.surname].filter(Boolean).join(' ')
    : referral.parent_1_name;
  const relationCode = fileRelative?.relation_code ?? referral.relative_relation_code;
  const relationKey = medcomRelationKey(relationCode);
  const showRelativeCpr = referral.has_parent_1_cpr || Boolean(fileRelative?.has_person_identifier);
  const senderAddress = fileSender ? formatLetterSenderAddress(fileSender) : null;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  return (
    <Card className="mt-4" data-testid="referral-overview">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
        aria-label={collapsed ? t('referrals.overview_expand') : t('referrals.overview_collapse')}
        className="flex min-h-10 w-full items-center justify-between text-left hover:bg-ds-row-hover"
      >
        <span className="text-xs font-medium uppercase tracking-wide text-ds-muted">
          {t('referrals.overview_title')}
        </span>
        {collapsed ? (
          <ChevronDown className="h-4 w-4 text-ds-muted" />
        ) : (
          <ChevronUp className="h-4 w-4 text-ds-muted" />
        )}
      </button>
      {!collapsed && (
        <div className="mt-s4">
          <div className={referralLetterFrameClass} data-testid="referral-overview-frame">
            <OverviewGrid>
              <OverviewField label={t('referrals.section_sender')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">{fileSender?.organisation_name ?? referral.sender_hospital ?? '—'}</div>
                  <div className="text-xs text-ds-muted">
                    {[fileSender?.department_name ?? referral.sender_department, fileSender?.unit_name]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </div>
                  {senderAddress && <div className="text-xs text-ds-muted">{senderAddress}</div>}
                  {fileSender?.telephone && (
                    <div className="text-xs text-ds-muted">
                      {t('referrals.medcom_phone_work', 'Arbejde')}: {fileSender.telephone}
                    </div>
                  )}
                  <div className="text-xs text-ds-muted">
                    {formatReferralDateTime(referral.sent_at) ?? '—'}
                  </div>
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_referring_doctor')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">
                    {fileReferrer?.person_name ?? referral.referring_doctor_name ?? '—'}
                  </div>
                  <IdentifierLine
                    identifierCode={senderIdentity.identifierCode}
                    identifier={senderIdentity.identifier}
                  />
                  {fileReferrer?.identifier ? (
                    <IdentifierLine
                      identifierCode={fileReferrer.identifier_code}
                      identifier={fileReferrer.identifier}
                      fallbackIdentifierCode={senderIdentity.identifierCode}
                    />
                  ) : null}
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_patient')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">
                    {referral.first_name} {referral.last_name}
                  </div>
                  {referral.has_cpr && (
                    <ReferralCprBadge
                      referralId={referral.id}
                      subject="patient"
                      canReveal={canRevealCpr}
                      replacement={replacementCpr}
                      autoReveal={canRevealCpr}
                    />
                  )}
                  <div className="text-xs text-ds-muted">{referral.phone ?? '—'}</div>
                  <div className="text-xs text-ds-muted">{referral.email ?? '—'}</div>
                </div>
              </OverviewField>
              <OverviewField label={medcom ? t('referrals.medcom_section_relative') : t('referrals.section_parents')}>
                {medcom ? (
                  <div className="space-y-1">
                    <div className="text-sm font-medium" data-testid="referral-overview-relative-name">
                      {relativeName ?? '—'}
                    </div>
                    {relationKey && (
                      <div className="text-xs text-ds-muted">{t(relationKey)}</div>
                    )}
                    {showRelativeCpr && (
                      <ReferralCprBadge
                        referralId={referral.id}
                        subject="parent_1"
                        canReveal={canRevealCpr}
                        autoReveal={canRevealCpr}
                      />
                    )}
                    {!relativeName && !showRelativeCpr && (
                      <div className="text-xs text-ds-muted">—</div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <ParentLine
                      name={referral.parent_1_name}
                      hasCpr={referral.has_parent_1_cpr}
                      cprBadge={
                        <ReferralCprBadge
                          referralId={referral.id}
                          subject="parent_1"
                          canReveal={canRevealCpr}
                        />
                      }
                    />
                    <ParentLine
                      name={referral.parent_2_name}
                      hasCpr={referral.has_parent_2_cpr}
                      cprBadge={
                        <ReferralCprBadge
                          referralId={referral.id}
                          subject="parent_2"
                          canReveal={canRevealCpr}
                        />
                      }
                    />
                    {!referral.parent_1_name &&
                      !referral.parent_2_name &&
                      !referral.has_parent_1_cpr &&
                      !referral.has_parent_2_cpr && <div className="text-xs text-ds-muted">—</div>}
                  </div>
                )}
              </OverviewField>
              {medcom && (
                <OverviewField label={t('referrals.medcom_section_technical')}>
                  <div className="space-y-1 text-xs text-ds-muted">
                    <div>
                      <span className="font-medium text-ds-text">{t('referrals.medcom_field_envelope_id', 'Kuvert-id')}: </span>
                      <span className="font-mono">{letterQuery.data?.envelope_identifier ?? '—'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-ds-text">{t('referrals.medcom_field_letter_id')}: </span>
                      <span className="font-mono">{referral.medcom_letter_identifier ?? '—'}</span>
                    </div>
                  </div>
                </OverviewField>
              )}
              <OverviewField label={t('referrals.field_ambulant')}>
                <div className="text-sm font-medium">
                  {referral.ambulant === true
                    ? t('referrals.ambulant_yes')
                    : referral.ambulant === false
                      ? t('referrals.ambulant_no')
                      : '—'}
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_referrer')}>
                <div className="space-y-1">
                  <div className="text-sm font-medium">{referral.referrer_name ?? '—'}</div>
                  <div className="text-xs text-ds-muted">
                    {referral.referrer_type
                      ? t(`referrals.referrer_type_${referral.referrer_type}`)
                      : '—'}
                  </div>
                  {referral.referrer_reference && (
                    <div className="text-xs text-ds-muted">Ref: {referral.referrer_reference}</div>
                  )}
                </div>
              </OverviewField>
              <OverviewField label={t('referrals.section_details')}>
                <div className="space-y-1">
                  {referral.reason && (
                    <div className="text-xs text-ds-muted">{referral.reason}</div>
                  )}
                  {referral.notes && <div className="text-xs text-ds-muted">{referral.notes}</div>}
                  {!referral.reason && !referral.notes && (
                    <div className="text-xs text-ds-muted">—</div>
                  )}
                </div>
              </OverviewField>
              <OverviewField label="Administrativ information">
                <div className="space-y-1">
                  {referral.secretary_name && (
                    <div className="text-xs text-ds-muted">Sekr: {referral.secretary_name}</div>
                  )}
                  {referral.psychologist_name && (
                    <div className="text-xs text-ds-muted">Psyk: {referral.psychologist_name}</div>
                  )}
                  {referral.id_number && (
                    <div className="text-xs text-ds-muted">ID: {referral.id_number}</div>
                  )}
                  {referral.case_number && (
                    <div className="text-xs text-ds-muted">Sag: {referral.case_number}</div>
                  )}
                  {!referral.secretary_name &&
                    !referral.psychologist_name &&
                    !referral.id_number &&
                    !referral.case_number && <div className="text-xs text-ds-muted">—</div>}
                </div>
              </OverviewField>
            </OverviewGrid>
          </div>
        </div>
      )}
    </Card>
  );
}

function OverviewGrid({ children }: { children: ReactNode }) {
  const items = Children.toArray(children);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4">
      {items.map((child, index) => (
        <div
          key={index}
          className={cn('min-w-0 px-6 py-5', referralOverviewRuleClass(index, items.length))}
        >
          {child}
        </div>
      ))}
    </div>
  );
}

function OverviewField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-ds-muted">{label}</span>
      </div>
      <div className="text-sm text-ds-text">{children}</div>
    </div>
  );
}

function ParentLine({
  name,
  hasCpr,
  cprBadge,
}: {
  name: string | null;
  hasCpr: boolean;
  cprBadge: ReactNode;
}) {
  if (!name && !hasCpr) return null;
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">{name ?? '—'}</div>
      {hasCpr && cprBadge}
    </div>
  );
}
